import { chunkUploadInit, chunkUploadSlice, chunkUploadComplete } from '@/api/open'
import { getFolders } from '@/api/local-material'
import { getWorkers } from '@/api/open'
import md5 from 'js-md5'

const CHUNK_SIZE = 5 * 1024 * 1024 // 5MB
const MAX_CONCURRENT = 3 // 最大并发上传数
const MAX_CHUNK_RETRIES = 3 // 单分片最大重试次数
const RETRY_BASE_DELAY = 1000 // 重试基础延迟(ms)

export default {
    name: 'batch-upload',
    props: {
        showBatchUpload: {
            type: Boolean,
            required: true
        },
        defaultPid: {
            type: [Number, String],
            default: null
        },
    },
    data() {
        return {
            pid: null,
            workerId: null,
            workers: [],
            treeData: [],
            replaceFields: {
                title: 'name',
                key: 'id',
                value: 'id'
            },
            uploadQueue: [], // 上传队列
            isProcessing: false, // 是否正在处理队列
        }
    },
    computed: {
        completedCount() {
            return this.uploadQueue.filter(i => i.status === 'success' || i.status === 'error').length
        }
    },
    mounted() {
        this.pid = this.defaultPid || null
        this.loadTreeData()
        this.loadWorkers()
    },
    methods: {
        close() {
            this.$emit('update:showBatchUpload', false)
            // 如果有成功的素材，触发刷新
            if (this.uploadQueue.some(i => i.status === 'success')) {
                this.$emit('success')
            }
        },

        async loadTreeData() {
            try {
                const res = await getFolders()
                this.treeData = res.data.list || []
            } catch (e) {
                console.error('加载目录失败', e)
            }
        },

        async loadWorkers() {
            try {
                const res = await getWorkers({ type: 3 })
                this.workers = res.data.list || []
            } catch (e) {
                console.error('加载上传人失败', e)
            }
        },

        beforeUpload(file) {
            // 校验
            if (file.type.startsWith('video/')) {
                if (file.size / 1024 / 1024 > 2000) {
                    this.$message.error(`${file.name} 超过2GB限制`)
                    return false
                }
            } else if (file.type.startsWith('image/')) {
                if (file.size / 1024 / 1024 > 10) {
                    this.$message.error(`${file.name} 图片超过10MB限制`)
                    return false
                }
            } else {
                this.$message.error(`${file.name} 不支持的文件类型`)
                return false
            }

            if (!this.pid) {
                this.$message.error('请先选择目录')
                return false
            }
            if (!this.workerId) {
                this.$message.error('请先选择上传人')
                return false
            }

            // 加入队列
            this.uploadQueue.push({
                uid: Date.now() + '_' + Math.random().toString(36).substr(2, 9),
                file: file,
                name: file.name,
                size: file.size,
                percent: 0,
                status: 'pending', // pending / uploading / success / error
                retryHint: '',
                materialId: null,
                uploadId: null,
                uploadBase: null,
                sign: null,
                totalChunks: 0,
                md5: null,
                width: null,
                height: null,
            })

            // 启动队列处理
            this.processQueue()
            return false // 阻止 ant-design 自动上传
        },

        async processQueue() {
            if (this.isProcessing) return
            this.isProcessing = true

            // 当正在上传的数量低于并发上限时持续调度
            while (this.uploadQueue.filter(i => i.status === 'uploading').length < MAX_CONCURRENT) {
                // 找下一个待上传的
                const next = this.uploadQueue.find(i => i.status === 'pending')
                if (!next) break

                // 开始上传（uploadFile 内部同步置为 uploading，保证并发计数正确）
                this.uploadFile(next)
            }

            this.isProcessing = false
        },

        async uploadFile(queueItem) {
            const file = queueItem.file

            try {
                queueItem.status = 'uploading'
                queueItem.percent = 0
                queueItem.retryHint = '计算文件信息...'

                // 1. 计算 MD5 和宽高
                const fileMd5 = await this.calculateMD5(file, queueItem.uid)
                queueItem.md5 = fileMd5

                let width = 0, height = 0
                if (file.type.startsWith('video/')) {
                    const meta = await this.getVideoMetadata(file)
                    width = meta.width
                    height = meta.height
                } else if (file.type.startsWith('image/')) {
                    const meta = await this.getImageMetadata(file)
                    width = meta.width
                    height = meta.height
                }
                queueItem.width = width
                queueItem.height = height

                queueItem.retryHint = ''

                // 2. 初始化分片上传
                const initRes = await chunkUploadInit({
                    name: file.name,
                    type: file.type.startsWith('video/') ? 3 : 2,
                    md5: fileMd5,
                    upload_type: 'chunk',
                    size: file.size,
                    width: width,
                    height: height,
                    pid: this.pid,
                    worker_id: this.workerId,
                    admin_id: JSON.parse(localStorage.getItem('user') || '{}').id || 0,
                })

                if (!initRes || initRes.code !== 0) {
                    throw new Error(initRes?.message || '初始化上传失败')
                }

                // 命中秒传
                if (initRes.data.is_quick_upload) {
                    queueItem.status = 'success'
                    queueItem.percent = 100
                    queueItem.materialId = '秒传'
                    this.$message.success(`${file.name} 秒传成功`)
                    this.processQueue()
                    return
                }

                queueItem.uploadId = initRes.data.uploadId
                queueItem.uploadBase = initRes.data.uploadBase
                queueItem.sign = initRes.data.sign
                queueItem.totalChunks = initRes.data.totalChunks

                // 3. 逐片上传
                const totalChunks = queueItem.totalChunks
                for (let i = 0; i < totalChunks; i++) {
                    const start = i * CHUNK_SIZE
                    const end = Math.min(start + CHUNK_SIZE, file.size)
                    const chunk = file.slice(start, end)
                    const chunkMd5 = md5(await chunk.arrayBuffer())

                    let success = false
                    for (let retry = 0; retry <= MAX_CHUNK_RETRIES; retry++) {
                        try {
                            const res = await chunkUploadSlice(queueItem.uploadBase, chunk, {
                                uploadId: queueItem.uploadId,
                                chunkIndex: i,
                                totalChunks: totalChunks,
                                chunkMd5: chunkMd5,
                                t: queueItem.sign.t,
                                sign: queueItem.sign.sign,
                                code: queueItem.sign.code,
                            })

                            if (res.code === 0) {
                                success = true
                                break
                            } else if (res.code === 401) {
                                // 签名错误不重试
                                throw new Error('签名验证失败')
                            } else {
                                throw new Error(res.message || '分片上传失败')
                            }
                        } catch (err) {
                            if (retry < MAX_CHUNK_RETRIES) {
                                const delay = RETRY_BASE_DELAY * Math.pow(2, retry)
                                queueItem.retryHint = `分片 ${i + 1}/${totalChunks} 失败，${delay / 1000}s 后重试...`
                                await this.sleep(delay)
                            } else {
                                throw new Error(`分片 ${i + 1}/${totalChunks} 上传失败：${err.message}`)
                            }
                        }
                    }

                    if (!success) {
                        throw new Error(`分片 ${i + 1}/${totalChunks} 上传失败`)
                    }

                    // 更新进度
                    queueItem.retryHint = ''
                    queueItem.percent = Math.floor(((i + 1) / totalChunks) * 95) // 留5%给合并
                }

                // 4. 合并完成
                queueItem.retryHint = '合并中...'
                const completeRes = await chunkUploadComplete(queueItem.uploadBase, {
                    uploadId: queueItem.uploadId,
                    t: queueItem.sign.t,
                    sign: queueItem.sign.sign,
                    code: queueItem.sign.code,
                })

                if (completeRes.code !== 0) {
                    throw new Error(completeRes.message || '合并失败')
                }

                queueItem.status = 'success'
                queueItem.percent = 100
                queueItem.retryHint = ''
                queueItem.materialId = completeRes.data.material_id
                this.$message.success(`${file.name} 上传成功`)

            } catch (err) {
                console.error(`[批量上传] ${file.name} 失败:`, err)
                queueItem.status = 'error'
                queueItem.retryHint = err.message || '上传失败'
            }

            // 继续处理队列
            this.processQueue()
        },

        retryUpload(index) {
            const item = this.uploadQueue[index]
            item.status = 'pending'
            item.percent = 0
            item.retryHint = ''
            item.uploadId = null
            item.uploadBase = null
            item.sign = null
            item.totalChunks = 0
            this.processQueue()
        },

        removeFromQueue(index) {
            this.uploadQueue.splice(index, 1)
        },

        calculateMD5(file, uid) {
            return new Promise((resolve, reject) => {
                const worker = new Worker('/workers/md5.worker.js')
                worker.onmessage = (event) => {
                    const { type, percent, md5: fileMd5, message } = event.data
                    if (type === 'progress') {
                        // 计算阶段进度单独显示，不占满0-95%
                        const item = this.uploadQueue.find(i => i.uid === uid)
                        if (item) {
                            item.retryHint = `计算文件MD5 ${percent}%...`
                        }
                    } else if (type === 'complete') {
                        worker.terminate()
                        resolve(fileMd5)
                    } else if (type === 'error') {
                        worker.terminate()
                        reject(new Error(message || 'MD5计算失败'))
                    }
                }
                worker.onerror = () => {
                    worker.terminate()
                    reject(new Error('MD5 Worker 错误'))
                }
                worker.postMessage({ file, uid })
            })
        },

        getVideoMetadata(file) {
            return new Promise((resolve) => {
                const video = document.createElement('video')
                video.preload = 'metadata'
                video.onloadedmetadata = () => {
                    resolve({ width: video.videoWidth, height: video.videoHeight })
                    URL.revokeObjectURL(video.src)
                }
                video.onerror = () => {
                    resolve({ width: 0, height: 0 })
                    URL.revokeObjectURL(video.src)
                }
                video.src = URL.createObjectURL(file)
            })
        },

        getImageMetadata(file) {
            return new Promise((resolve) => {
                const img = new Image()
                img.onload = () => {
                    resolve({ width: img.width, height: img.height })
                    URL.revokeObjectURL(img.src)
                }
                img.onerror = () => {
                    resolve({ width: 0, height: 0 })
                    URL.revokeObjectURL(img.src)
                }
                img.src = URL.createObjectURL(file)
            })
        },

        formatSize(bytes) {
            if (!bytes) return '0 B'
            const units = ['B', 'KB', 'MB', 'GB']
            let i = 0
            let size = bytes
            while (size >= 1024 && i < units.length - 1) {
                size /= 1024
                i++
            }
            return size.toFixed(1) + ' ' + units[i]
        },

        sleep(ms) {
            return new Promise(resolve => setTimeout(resolve, ms))
        },
    }
}
